import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router";
import { DataTable } from "primereact/datatable";
import type { DataTableValueArray } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { useHideTimeSpent } from "sj-ui-toolkit/src/integrations";
import {
  fetchTournamentLeaderboardAsync,
  exportTournamentLeaderboardAsync,
  type TournamentParticipant,
} from "@/api/tournaments";
import { skeletonRows, withSkeleton } from "@/helpers/tableBody";

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_ROW_COUNT = 10;

// ─── Column body templates ────────────────────────────────────────────────────

const rankBody = withSkeleton<TournamentParticipant>(row => String(row.score ?? "—"), "4rem");
const nameBody = withSkeleton<TournamentParticipant>(row => row.name ?? "—", "10rem");
const emailBody = withSkeleton<TournamentParticipant>(row => row.email ?? "—", "12rem");
const completedBody = withSkeleton<TournamentParticipant>(
  row => String(row.completedExercises ?? "—"),
  "6rem"
);
const ratingBody = withSkeleton<TournamentParticipant>(row => String(row.rating ?? "—"), "5rem");
const timeBody = withSkeleton<TournamentParticipant>(row => row.time ?? "—", "6rem");

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function TournamentLeaderboard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  const tournamentName = (location.state as { tournamentName?: string } | null)?.tournamentName;

  useBreadcrumb({
    label: tournamentName ?? "Leaderboard",
    route: AdminAppRoutes.routes.tournaments.routes.leaderboard.route.replace(":id", id ?? ""),
    display: "page",
  });

  const hideTimeSpent = useHideTimeSpent();
  const [participants, setParticipants] = useState<TournamentParticipant[] | "loading">("loading");
  const [isExporting, setIsExporting] = useState(false);
  const [filters, setFilters] = useState({
    global: { value: "", matchMode: FilterMatchMode.CONTAINS },
  });

  const hasLoadedRef = useRef(false);

  // ─── Load data on mount ────────────────────────────────────────────────

  useEffect(() => {
    if (hasLoadedRef.current || !id) return;
    hasLoadedRef.current = true;

    const load = async () => {
      try {
        const data = await fetchTournamentLeaderboardAsync(id);
        setParticipants(data.participants);
      } catch (error) {
        console.error("Error fetching tournament leaderboard:", error);
        showToast({ severity: "error", detail: "Failed to load tournament data" });
        setParticipants([]);
      }
    };

    load();
  }, [id, showToast]);

  // ─── Export ────────────────────────────────────────────────────────────

  const handleExport = async () => {
    if (!id || isExporting) return;
    setIsExporting(true);
    try {
      await exportTournamentLeaderboardAsync(id);
    } catch (error) {
      console.error("Error exporting tournament leaderboard:", error);
      showToast({ severity: "error", detail: "Failed to export tournament leaderboard" });
    } finally {
      setIsExporting(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────

  const isLoading = participants === "loading";
  const skeletonData = skeletonRows<TournamentParticipant>(DEFAULT_ROW_COUNT);
  const tableValue = isLoading ? skeletonData : (participants as TournamentParticipant[]);

  return (
    <div>
      <div className="mb-4">
        <div className="flex justify-between items-center">
          <h1>{tournamentName ? `Leaderboard - ${tournamentName}` : "Tournament Leaderboard"}</h1>
          <Button
            icon="pi pi-arrow-left"
            label="Back to Tournaments"
            severity="secondary"
            data-testid="back-to-tournaments-button"
            onClick={() => navigate(AdminAppRoutes.routes.tournaments.route)}
          />
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            placeholder="Search Participants"
            aria-label="Search participants"
            data-testid="leaderboard-search-input"
            value={filters.global.value}
            onChange={e =>
              setFilters({ ...filters, global: { ...filters.global, value: e.target.value } })
            }
          />
        </IconField>
        <Button
          icon="pi pi-download"
          label="Export Data"
          aria-label="Download Leaderboard"
          data-testid="leaderboard-export-button"
          loading={isExporting}
          disabled={isExporting}
          onClick={() => void handleExport()}
        />
      </div>

      <DataTable
        value={tableValue as unknown as DataTableValueArray}
        emptyMessage="No participants found."
        aria-label="Tournament leaderboard"
        data-testid="tournament-leaderboard-table"
        filters={filters}
        globalFilterFields={["name", "email"]}
        tableClassName="dt-responsive-text"
      >
        <Column field="score" header="Rank" body={rankBody} />
        <Column field="name" header="Name" body={nameBody} />
        <Column field="email" header="Email" body={emailBody} />
        <Column field="completedExercises" header="Completed Exercises" body={completedBody} />
        <Column field="rating" header="Total Points" body={ratingBody} />
        {!hideTimeSpent && <Column field="time" header="Total Time" body={timeBody} />}
      </DataTable>
    </div>
  );
}
